import { Web3DViewer } from '@technology/web3d';
import { ThreeJsManager } from '@trimble/web3d-utils';
import { EXTERNAL_FRUSTUM_MESH_TYPE } from '@trimble/types';
import { PositioningData } from '@trimble/web3d-providers';
import { createRoot } from 'solid-js';
import { FrustumCapability } from './frustum-capability';
import { CapabilityCategory } from './base/models/capability-category';
import { SingleCapabilityBase } from './base/single-capability-base';

interface OwnedFrustum {
  capability: FrustumCapability;
  dispose(): void;
}

export type CrossPaneFrustumSnapshot = {
  id: string;
  data: Omit<PositioningData, 'cameraPosition'>;
};

/**
 * Manages frustum cones/wedges for **other** panes, rendered inside the current pane's viewer.
 * Each snapshot id maps to a dedicated {@link FrustumCapability} instance so multiple
 * cross-pane frustums can coexist. The render style (`flat` for 2D, `cone` for 3D) is
 * set once at construction and forwarded to every child frustum.
 */
export class CrossPaneFrustumsCapability extends SingleCapabilityBase {
  public override category: CapabilityCategory = CapabilityCategory.Command;

  private readonly frustums = new Map<string, OwnedFrustum>();
  private readonly useMapHeight: boolean;
  private readonly renderStyle: 'flat' | 'cone';

  constructor(
    viewer: Web3DViewer,
    private readonly threeJsManager: ThreeJsManager,
    options?: { useMapHeight?: boolean; renderStyle?: 'flat' | 'cone' }
  ) {
    super(viewer);
    this.useMapHeight = options?.useMapHeight ?? true;
    this.renderStyle = options?.renderStyle ?? 'flat';
  }

  public override deactivate(): boolean {
    const entries = [...this.frustums.values()];
    this.frustums.clear();
    for (const { capability, dispose } of entries) {
      capability.deactivate();
      // Synchronous mesh disposal to avoid a flicker before the async cleanup completes.
      capability.unloadFrustum();
      void capability.cleanup();
      dispose();
    }
    return super.deactivate();
  }

  public async updateFrustums(
    snapshots: CrossPaneFrustumSnapshot[]
  ): Promise<void> {
    if (!this.isActive()) {
      return;
    }
    const nextIds = new Set<string>();

    for (const snapshot of snapshots) {
      if (!snapshot.data.targetPosition) {
        continue;
      }
      nextIds.add(snapshot.id);
      const { capability } = this.ensureFrustum(snapshot.id);
      capability.setFrustumPosition(snapshot.data);
    }

    const idsToRemove = [...this.frustums.keys()].filter(
      (id) => !nextIds.has(id)
    );
    await Promise.all(idsToRemove.map((id) => this.removeFrustum(id)));

    this.threeJsManager.redraw();
  }

  public async clearFrustums(): Promise<void> {
    const ids = [...this.frustums.keys()];
    await Promise.all(ids.map((id) => this.removeFrustum(id)));
  }

  public override async cleanup(): Promise<void> {
    await this.clearFrustums();
  }

  private ensureFrustum(id: string): OwnedFrustum {
    const existing = this.frustums.get(id);
    if (existing) {
      return existing;
    }

    let frustum!: FrustumCapability;
    const dispose = createRoot((d) => {
      frustum = new FrustumCapability(this.viewer, this.threeJsManager, {
        useMapHeight: this.useMapHeight,
        renderStyle: this.renderStyle,
        meshUserDataType: EXTERNAL_FRUSTUM_MESH_TYPE,
      });
      return d;
    });
    frustum.enable();
    frustum.activate();
    const owned: OwnedFrustum = { capability: frustum, dispose };
    this.frustums.set(id, owned);
    return owned;
  }

  private async removeFrustum(id: string): Promise<void> {
    const entry = this.frustums.get(id);
    if (!entry) {
      return;
    }
    entry.capability.deactivate();
    await entry.capability.cleanup();
    entry.dispose();
    this.frustums.delete(id);
  }
}
